import { useState, useEffect, useCallback } from 'react';
import { EmbedToken } from '../types';

const API_URL = process.env.REACT_APP_API_URL || '';

export function useTokens(email: string | undefined) {
  const [tokens, setTokens] = useState<EmbedToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTokens = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/tokens?email=${encodeURIComponent(email)}`);
      if (!res.ok) throw new Error('Failed to fetch tokens');
      const data = await res.json();
      setTokens(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  const createToken = async (label: string, name: string) => {
    if (!email) return;
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/tokens`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, label }),
      });
      if (!res.ok) throw new Error('Failed to create token');
      const newToken = await res.json();
      setTokens(prev => [...prev, newToken]);
      return newToken;
    } catch (err: any) {
      setError(err.message);
    }
  };

  const deleteToken = async (token: string) => {
    if (!email) return;
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/tokens/${token}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error('Failed to delete token');
      setTokens(prev => prev.filter(t => t.token !== token));
    } catch (err: any) {
      setError(err.message);
    }
  };

  return { tokens, loading, error, createToken, deleteToken, refetch: fetchTokens };
}
